import { type ReactNode, useMemo } from "react";
import {
  type IPriceLineItem,
  type ISummaryLineItem,
  SummaryLineType,
} from "@b2bportal/core-types";
import type { HotelItinerary } from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import dayjs from "dayjs";
import { TaxesInfoPopover } from "@/components/TaxesInfoPopover";
import { defaultPointsExchange } from "@/utils/points";
import { getPriceBreakdownFromReservation } from "@/utils/pricing";
import { getReservationPayments } from "@/utils/trips";

type PaymentAmount = {
  amount: number;
  currency: string;
};

type CardPaymentLineItem = {
  type: "Card";
  value: {
    paymentAmount: PaymentAmount;
    paymentId: string;
  };
};

type LoyaltyPointsPaymentLineItem = {
  type: "LoyaltyPoints";
  value: {
    cashValue: PaymentAmount;
  };
};

export type PaymentLineItemValue =
  | CardPaymentLineItem
  | LoyaltyPointsPaymentLineItem;

export type TaxesAndFeesLineItem = {
  label: string;
  value: string;
  labelEndAdornment?: ReactNode;
};
export type PriceLineItemWithTaxesAndFees = Omit<
  IPriceLineItem,
  "taxesAndFees"
> & {
  taxesAndFees?: TaxesAndFeesLineItem[];
};

export type ReservationBreakdown = {
  title?: string;
  baseAmount: {
    label: string;
    value: string;
  };
  payNowTaxesAndFees: TaxesAndFeesLineItem[];
  payLaterTaxesAndFees: TaxesAndFeesLineItem[];
  disclaimer: string | null;
  summaryPoints: {
    label: string;
    value: number;
  } | null;
  summaryCharged: {
    label: string;
    value: string;
  } | null;
  payAtPropertyTotal: {
    label: string;
    value: string;
  };
  tripTotal: {
    label: string;
    value: string;
  };
};

export const useHotelReservationPayment = ({
  itinerary,
}: {
  itinerary: HotelItinerary;
}) => {
  const { t, formatNumber, formatFiatCurrency } = useI18nContext();

  const nightCount = useMemo(() => {
    const { checkInDate, checkOutDate } = itinerary.reservation;

    return dayjs(checkOutDate).diff(dayjs(checkInDate), "day");
  }, [itinerary]);

  const reservationPaymentLineItems: PaymentLineItemValue[] = useMemo(() => {
    if (itinerary.paymentBreakdown == null) {
      console.error(
        `paymentBreakdown is missing from reservation ${itinerary.reservation.reservationBookingId}`
      );
      return [];
    }
    return (itinerary.paymentBreakdown.paymentLineItems ||
      []) as PaymentLineItemValue[];
  }, [itinerary]);

  const pricingBreakdown = useMemo(() => {
    return getPriceBreakdownFromReservation(
      itinerary.reservation.pricing,
      reservationPaymentLineItems,
      nightCount
    );
  }, [reservationPaymentLineItems, itinerary, nightCount]);

  const reservationPayments = useMemo(
    () => getReservationPayments({ reservation: itinerary }),
    [itinerary]
  );

  const summaryCharged = useMemo(() => {
    const payment = reservationPayments.card;
    if (payment != null) {
      const amount = payment.amount;
      return {
        label: t("hotels.priceBreakdown.alreadyPaidLabel"),
        value: formatFiatCurrency({
          currencyCode: payment.currency,
          value: amount,
        }),
        type: SummaryLineType.CARD_PAYMENT,
      };
    }
    return null;
  }, [reservationPayments, formatFiatCurrency, t]);

  const summaryPoints = useMemo(() => {
    const pointsValue = reservationPayments.rewards;
    if (pointsValue != null && pointsValue > 0) {
      return {
        label: t("hotels.priceBreakdown.pointsApplied"),
        value: pointsValue,
        type: SummaryLineType.TOTAL,
      };
    }
    return null;
  }, [reservationPayments, formatNumber, t]);

  const disclaimer = useMemo(() => {
    if (
      pricingBreakdown.payLaterTotal &&
      pricingBreakdown.payLaterTotal?.value > 0
    ) {
      if (
        pricingBreakdown.payLaterTotalLocalCurrency &&
        pricingBreakdown.payLaterTotal.currencyCode !==
          pricingBreakdown.payLaterTotalLocalCurrency.currencyCode
      ) {
        return t("hotels.priceBreakdown.disclaimerWithPrice", {
          price: formatFiatCurrency(
            pricingBreakdown.payLaterTotalLocalCurrency
          ),
        });
      } else {
        return t("hotels.priceBreakdown.disclaimerNoPrice");
      }
    }
    return null;
  }, [pricingBreakdown, formatFiatCurrency, t]);

  const breakdown: ReservationBreakdown = useMemo(() => {
    return {
      title: t("hotels.priceBreakdown.title"),
      baseAmount: {
        label: t("hotels.priceBreakdown.hotelStayYieldSummary", {
          count: nightCount,
        }),
        value: formatFiatCurrency(pricingBreakdown.sellRate.fiat),
      },
      payNowTaxesAndFees: [
        {
          label: t("hotels.priceBreakdown.rateTaxesAndFeesLabel"),
          fiat: pricingBreakdown.taxesAndFees.rateTaxesAndFees,
          labelEndAdornment: <TaxesInfoPopover />,
        },
      ]
        .filter(({ fiat }) => fiat && fiat.value > 0)
        .map(({ label, fiat, labelEndAdornment }) => ({
          label,
          value: formatFiatCurrency(fiat),
          labelEndAdornment: <TaxesInfoPopover />,
        })),
      payLaterTaxesAndFees: [
        {
          label: t("hotels.priceBreakdown.mandatoryTaxLabel"),
          fiat: pricingBreakdown.taxesAndFees.mandatoryTax,
        },
        {
          label: t("hotels.priceBreakdown.mandatoryFeeLabel"),
          fiat: pricingBreakdown.taxesAndFees.mandatoryFee,
        },
        {
          label: t("hotels.priceBreakdown.resortFeeLabel"),
          fiat: pricingBreakdown.taxesAndFees.resortFee,
        },
      ]
        .filter(({ fiat }) => fiat && fiat.value > 0)
        .map(({ label, fiat }) => ({
          label,
          value: formatFiatCurrency(fiat),
        })),

      disclaimer,
      summaryPoints,
      summaryCharged,
      payAtPropertyTotal: {
        label: t("hotels.priceBreakdown.payAtPropertyLabel"),
        value: formatFiatCurrency(pricingBreakdown.payLaterTotal),
      },
      tripTotal: {
        label: t("hotels.priceBreakdown.tripTotal"),
        value: formatFiatCurrency(pricingBreakdown.amountDue),
      },
    };
  }, [
    summaryCharged,
    summaryPoints,
    pricingBreakdown,
    disclaimer,
    nightCount,
    formatFiatCurrency,
    t,
  ]);

  return {
    nightCount,
    breakdown,
  };
};
